import { useEffect, useCallback, useRef } from "preact/hooks";
import { Bookmark } from "../utils/api";

interface KeyboardShortcutsProps {
  bookmarks: Bookmark[];
  onSearch: () => void;
  onAdd: () => void;
  onSelect: (bookmark: Bookmark) => void;
  isModalOpen: boolean;
}

export function useKeyboardShortcuts({
  bookmarks,
  onSearch,
  onAdd,
  onSelect,
  isModalOpen,
}: KeyboardShortcutsProps) {
  const selectedIndexRef = useRef(0);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (isModalOpen) return;

      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        onSearch();
        return;
      }

      if ((e.metaKey || e.ctrlKey) && e.key === "n") {
        e.preventDefault();
        onAdd();
        return;
      }

      if (e.key === "Escape") {
        selectedIndexRef.current = 0;
        return;
      }

      if (bookmarks.length === 0) return;

      if (e.key === "j" || e.key === "ArrowDown") {
        e.preventDefault();
        selectedIndexRef.current = Math.min(
          selectedIndexRef.current + 1,
          bookmarks.length - 1
        );
      } else if (e.key === "k" || e.key === "ArrowUp") {
        e.preventDefault();
        selectedIndexRef.current = Math.max(selectedIndexRef.current - 1, 0);
      } else if (e.key === "Enter" || e.key === "o") {
        e.preventDefault();
        const selected = bookmarks[selectedIndexRef.current];
        if (selected) {
          window.open(selected.url, "_blank", "noopener,noreferrer");
        }
      }
    },
    [bookmarks, onSearch, onAdd, onSelect, isModalOpen]
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  return { selectedIndex: selectedIndexRef.current };
}
